/** Confidence check overlay — three-level self-assessment shown after answering a question correctly. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CircleHelp, ThumbsUp, PartyPopper } from 'lucide-react';
import { AnimatedKbd, useShortcutOverlay } from './ShortcutOverlay';
import { RippleFlash } from './KeyboardRipple';

// ─── Types ──────────────────────────────────────────────────────────────────

export type ConfidenceLevel = 'low' | 'medium' | 'high';

interface ConfidenceCheckProps {
  show: boolean;
  onSelect: (level: ConfidenceLevel) => void;
}

// ─── Button config ──────────────────────────────────────────────────────────

const levels: { level: ConfidenceLevel; label: string; key: string; icon: React.ReactNode }[] = [
  { level: 'low', label: 'Not sure', key: '1', icon: <CircleHelp size={16} /> },
  { level: 'medium', label: 'Fairly sure', key: '2', icon: <ThumbsUp size={16} /> },
  { level: 'high', label: 'Very sure', key: '3', icon: <PartyPopper size={16} /> },
];

// ─── Component ──────────────────────────────────────────────────────────────

export function ConfidenceCheck({ show, onSelect }: ConfidenceCheckProps) {
  const { showShortcuts } = useShortcutOverlay();
  const [hoveredLevel, setHoveredLevel] = useState<ConfidenceLevel | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [rippleIndex, setRippleIndex] = useState<number | null>(null);
  const [rippleKey, setRippleKey] = useState(0);
  const rippleTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  const triggerRipple = useCallback((index: number) => {
    clearTimeout(rippleTimeout.current);
    setRippleIndex(index);
    setRippleKey((k) => k + 1);
    rippleTimeout.current = setTimeout(() => setRippleIndex(null), 400);
  }, []);

  // Reset state when overlay opens (derived state during render)
  const [prevShow, setPrevShow] = useState(show);
  if (show && !prevShow) {
    setSelectedIndex(null);
    setHoveredLevel(null);
    setRippleIndex(null);
  }
  if (show !== prevShow) {
    setPrevShow(show);
  }

  const handleSelect = useCallback(
    (index: number) => {
      if (selectedIndex !== null) return; // prevent double-select
      setSelectedIndex(index);
      triggerRipple(index);
      // Hold the selected state briefly so the user sees their choice
      setTimeout(() => onSelect(levels[index].level), 350);
    },
    [selectedIndex, triggerRipple, onSelect]
  );

  // Keyboard: 1/2/3 to select
  useEffect(() => {
    if (!show) return;

    function handleKey(e: KeyboardEvent) {
      const num = parseInt(e.key, 10);
      if (num >= 1 && num <= 3) {
        e.preventDefault();
        handleSelect(num - 1);
      }
    }

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [show, handleSelect]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="confidence-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.06)',
            pointerEvents: 'auto',
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            style={{
              padding: 'var(--f-space-6) var(--f-space-8)',
              borderRadius: 'var(--f-radius-xl)',
              backgroundColor: 'var(--f-bg)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(155, 107, 218, 0.08)',
              fontFamily: 'var(--f-font-sans)',
              textAlign: 'center',
              maxWidth: '560px',
              width: '100%',
              margin: '0 var(--f-space-4)',
            }}
          >
            <p
              style={{
                fontSize: 'var(--f-text-base)',
                fontWeight: 600,
                color: 'var(--f-fg)',
                marginBottom: 'var(--f-space-4)',
              }}
            >
              How confident are you?
            </p>

            <div style={{ display: 'flex', gap: 'var(--f-space-3)' }}>
              {levels.map(({ level, label, key: _key, icon }, index) => {
                const isSelected = selectedIndex === index;
                const isHighlighted = hoveredLevel === level || rippleIndex === index || isSelected;

                return (
                  <motion.button
                    key={level}
                    type="button"
                    onClick={() => handleSelect(index)}
                    onMouseEnter={() => setHoveredLevel(level)}
                    onMouseLeave={() => setHoveredLevel(null)}
                    whileHover={
                      !isSelected
                        ? { scale: 1.02, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }
                        : undefined
                    }
                    whileTap={!isSelected ? { scale: 0.98 } : undefined}
                    transition={{ type: 'spring', stiffness: 500, damping: 28 }}
                    style={{
                      flex: 1,
                      padding: 'var(--f-space-3) var(--f-space-2)',
                      borderRadius: 'var(--f-radius-lg)',
                      border: `2px solid ${isHighlighted ? 'var(--f-accent)' : 'var(--f-border)'}`,
                      backgroundColor: isSelected ? 'var(--f-accent-subtle)' : 'var(--f-bg-raised)',
                      color: 'var(--f-fg)',
                      fontSize: 'var(--f-text-sm)',
                      fontWeight: 500,
                      fontFamily: 'var(--f-font-sans)',
                      cursor: selectedIndex !== null ? 'default' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 'var(--f-space-2)',
                      position: 'relative',
                      overflow: 'visible',
                      transition: 'border-color 0.2s ease, background-color 0.2s ease',
                      outline: 'none',
                    }}
                  >
                    <RippleFlash active={rippleIndex === index} rippleKey={rippleKey} />
                    {icon}
                    <span>{label}</span>
                    <AnimatedKbd show={showShortcuts} position="corner">
                      {index + 1}
                    </AnimatedKbd>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
